import React from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, gql } from "@apollo/client";
import { SAUNA_TYPES } from "../utils/saunaTypes";

const ALL_AMENITIES = gql`
  query AllAmenities {
    allAmenities { nodes { id label } }
  }
`;

const RADIUS_OPTIONS = [
  { label: "10 mi", value: "10" },
  { label: "25 mi", value: "25" },
  { label: "50 mi", value: "50" },
  { label: "100 mi", value: "100" },
];

export default function FilterBar() {
  const [params, setParams] = useSearchParams();
  const { data: amenityData } = useQuery(ALL_AMENITIES);
  const amenities: { id: number; label: string }[] = amenityData?.allAmenities?.nodes ?? [];

  const selectedTypes = new Set((params.get("types") ?? "").split(",").filter(Boolean));
  const selectedAmenities = new Set(
    (params.get("amenities") ?? "").split(",").filter(Boolean).map(Number)
  );
  const hasLocation = !!(params.get("lat") && params.get("lng"));
  const radius = params.get("radius") ?? "100";
  const minCap = params.get("minCap") ?? "";
  const maxCap = params.get("maxCap") ?? "";
  const minPrice = params.get("minPrice") ?? "";
  const maxPrice = params.get("maxPrice") ?? "";

  const setParam = (key: string, value: string) => {
    setParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    });
  };

  const toggleType = (val: string) => {
    const next = new Set(selectedTypes);
    next.has(val) ? next.delete(val) : next.add(val);
    setParam("types", Array.from(next).join(","));
  };

  const toggleAmenity = (id: number) => {
    const next = new Set(selectedAmenities);
    next.has(id) ? next.delete(id) : next.add(id);
    setParam("amenities", Array.from(next).join(","));
  };

  const clearAll = () => {
    setParams(prev => {
      const next = new URLSearchParams(prev);
      ["types", "amenities", "minCap", "maxCap", "minPrice", "maxPrice", "radius"].forEach(k => next.delete(k));
      return next;
    });
  };

  const hasFilters = selectedTypes.size > 0 || selectedAmenities.size > 0 ||
    minCap || maxCap || minPrice || maxPrice ||
    (params.get("radius") !== null && params.get("radius") !== "100");

  return (
    <div style={barStyle}>
      <section style={sectionStyle}>
        <div style={labelStyle}>Sauna type</div>
        <div style={chipRowStyle}>
          {SAUNA_TYPES.map(t => (
            <button
              key={t.value}
              onClick={() => toggleType(t.value)}
              style={{ ...chipStyle, ...(selectedTypes.has(t.value) ? activeChipStyle : {}) }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </section>

      {amenities.length > 0 && (
        <section style={sectionStyle}>
          <div style={labelStyle}>Amenities</div>
          <div style={chipRowStyle}>
            {amenities.map(a => (
              <button
                key={a.id}
                onClick={() => toggleAmenity(a.id)}
                style={{ ...chipStyle, ...(selectedAmenities.has(a.id) ? activeChipStyle : {}) }}
              >
                {a.label.replace(/_/g, " ")}
              </button>
            ))}
          </div>
        </section>
      )}

      {hasLocation && (
        <section style={sectionStyle}>
          <div style={labelStyle}>Distance</div>
          <div style={chipRowStyle}>
            {RADIUS_OPTIONS.map(o => (
              <button
                key={o.value}
                onClick={() => setParam("radius", o.value)}
                style={{ ...chipStyle, ...(radius === o.value ? activeChipStyle : {}) }}
              >
                {o.label}
              </button>
            ))}
          </div>
        </section>
      )}

      <section style={{ ...sectionStyle, display: "flex", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={labelStyle}>Capacity</div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              type="number" min={1} placeholder="Min" value={minCap}
              onChange={e => setParam("minCap", e.target.value)}
              style={numInput}
            />
            <span style={{ color: "#9ca3af" }}>–</span>
            <input
              type="number" min={1} placeholder="Max" value={maxCap}
              onChange={e => setParam("maxCap", e.target.value)}
              style={numInput}
            />
            <span style={{ fontSize: 13, color: "#6b7280" }}>people</span>
          </div>
        </div>

        <div>
          <div style={labelStyle}>Price / hr ($)</div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              type="number" min={0} placeholder="Min" value={minPrice ? String(parseInt(minPrice) / 100) : ""}
              onChange={e => setParam("minPrice", e.target.value ? String(Math.round(parseFloat(e.target.value) * 100)) : "")}
              style={numInput}
            />
            <span style={{ color: "#9ca3af" }}>–</span>
            <input
              type="number" min={0} placeholder="Max" value={maxPrice ? String(parseInt(maxPrice) / 100) : ""}
              onChange={e => setParam("maxPrice", e.target.value ? String(Math.round(parseFloat(e.target.value) * 100)) : "")}
              style={numInput}
            />
          </div>
        </div>
      </section>

      {hasFilters && (
        <button onClick={clearAll} style={clearStyle}>Clear filters</button>
      )}
    </div>
  );
}

const barStyle: React.CSSProperties = {
  background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10,
  padding: "14px 16px", display: "flex", flexDirection: "column", gap: 14,
};
const sectionStyle: React.CSSProperties = {};
const labelStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: "#9ca3af", marginBottom: 6, letterSpacing: "0.05em" };
const chipRowStyle: React.CSSProperties = { display: "flex", flexWrap: "wrap", gap: 6 };
const chipStyle: React.CSSProperties = {
  padding: "5px 12px", border: "1px solid #d1d5db", borderRadius: 20,
  fontSize: 13, cursor: "pointer", background: "#fff", color: "#374151",
};
const activeChipStyle: React.CSSProperties = { background: "#2563eb", color: "#fff", borderColor: "#2563eb" };
const numInput: React.CSSProperties = {
  width: 70, padding: "7px 8px", border: "1px solid #d1d5db",
  borderRadius: 6, fontSize: 13,
};
const clearStyle: React.CSSProperties = {
  alignSelf: "flex-start", padding: "6px 12px", border: "1px solid #ef4444",
  borderRadius: 6, fontSize: 13, color: "#ef4444", background: "#fff", cursor: "pointer",
};
